import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const FIELD_COLORS = {
  pest365: '#2563eb',
  pest365Plus: '#8b5cf6',
  oneTime: '#f59e0b',
  initialServiceVisit: '#16a34a',
};

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365 Plus',
  oneTime: 'One-Time',
  initialServiceVisit: 'Initial Service Visit',
};

const styles = {
  card: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#6b7280',
    margin: '4px 0 16px 0',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 0',
    color: '#9ca3af',
    fontSize: 14,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 12,
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#374151',
  },
  legendSwatch: {
    width: 14,
    height: 3,
    borderRadius: 1,
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#1f2937',
    color: '#f9fafb',
    padding: '10px 14px',
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.5,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 9999,
    maxWidth: 240,
  },
  tooltipLabel: {
    fontWeight: 600,
    marginBottom: 4,
    fontSize: 13,
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
  },
  tooltipDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: 6,
    verticalAlign: 'middle',
  },
  medianBadge: {
    display: 'inline-block',
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: 13,
    color: '#374151',
    fontWeight: 500,
    marginTop: 8,
  },
};

export default function PriceMemoryChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const hasData = useMemo(() => {
    if (!data) return false;
    if (!data.durations || !data.survivalRate) return false;
    if (data.durations.length === 0 || data.survivalRate.length === 0) return false;
    return true;
  }, [data]);

  const fieldKeys = useMemo(() => {
    if (!hasData || !data.byField) return [];
    return Object.keys(data.byField).filter(
      (k) => FIELD_COLORS[k] && data.byField[k].durations && data.byField[k].survivalRate
    );
  }, [data, hasData]);

  useEffect(() => {
    if (!hasData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 380;
    const margin = { top: 20, right: 24, bottom: 44, left: 52 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const allDurations = [0, ...data.durations];
    fieldKeys.forEach((key) => {
      const fd = data.byField[key];
      if (fd.durations) {
        fd.durations.forEach((d) => {
          if (!allDurations.includes(d)) allDurations.push(d);
        });
      }
    });

    const maxDuration = d3.max(allDurations) || 10;

    const xScale = d3.scaleLinear().domain([0, maxDuration]).range([0, innerWidth]);
    const yScale = d3.scaleLinear().domain([0, 1]).range([innerHeight, 0]);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Grid lines
    g.append('g')
      .attr('class', 'grid-y')
      .selectAll('line')
      .data([0.25, 0.5, 0.75, 1.0])
      .join('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // 50% median reference dashed line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', yScale(0.5))
      .attr('y2', yScale(0.5))
      .attr('stroke', '#9ca3af')
      .attr('stroke-dasharray', '6,4')
      .attr('stroke-width', 1);

    g.append('text')
      .attr('x', innerWidth + 2)
      .attr('y', yScale(0.5))
      .attr('dy', '0.35em')
      .attr('font-size', 10)
      .attr('fill', '#9ca3af')
      .text('50%');

    // Shaded area under overall curve
    const overallPoints = buildStepPoints(data.durations, data.survivalRate, maxDuration);

    const areaGen = d3
      .area()
      .x((d) => xScale(d[0]))
      .y0(innerHeight)
      .y1((d) => yScale(d[1]));

    g.append('path')
      .datum(overallPoints)
      .attr('d', areaGen)
      .attr('fill', '#e5e7eb')
      .attr('opacity', 0.5);

    // Overall curve line
    const lineGen = d3
      .line()
      .x((d) => xScale(d[0]))
      .y((d) => yScale(d[1]));

    g.append('path')
      .datum(overallPoints)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4,3');

    // Per-field step function lines
    fieldKeys.forEach((key) => {
      const fd = data.byField[key];
      const pts = buildStepPoints(fd.durations, fd.survivalRate, maxDuration);

      g.append('path')
        .datum(pts)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', FIELD_COLORS[key])
        .attr('stroke-width', 2);
    });

    // Vertical median drop-lines for each field
    fieldKeys.forEach((key) => {
      const fd = data.byField[key];
      const median = fd.median;
      if (median == null || median <= 0) return;
      if (xScale(median) > innerWidth) return;

      g.append('line')
        .attr('x1', xScale(median))
        .attr('x2', xScale(median))
        .attr('y1', yScale(0.5))
        .attr('y2', innerHeight)
        .attr('stroke', FIELD_COLORS[key])
        .attr('stroke-dasharray', '3,3')
        .attr('stroke-width', 1)
        .attr('opacity', 0.6);

      g.append('text')
        .attr('x', xScale(median))
        .attr('y', innerHeight + 14)
        .attr('text-anchor', 'middle')
        .attr('font-size', 9)
        .attr('fill', FIELD_COLORS[key])
        .attr('font-weight', 600)
        .text(median);
    });

    // X axis
    const xAxis = d3
      .axisBottom(xScale)
      .ticks(Math.min(maxDuration, 10))
      .tickFormat((d) => d);

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis)
      .call((sel) => {
        sel.select('.domain').attr('stroke', '#d1d5db');
        sel.selectAll('.tick line').attr('stroke', '#d1d5db');
        sel.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', 11);
      });

    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + 38)
      .attr('text-anchor', 'middle')
      .attr('font-size', 11)
      .attr('fill', '#6b7280')
      .text('Duration (scrape periods)');

    // Y axis
    const yAxis = d3
      .axisLeft(yScale)
      .ticks(5)
      .tickFormat((d) => `${Math.round(d * 100)}%`);

    g.append('g')
      .call(yAxis)
      .call((sel) => {
        sel.select('.domain').attr('stroke', '#d1d5db');
        sel.selectAll('.tick line').attr('stroke', '#d1d5db');
        sel.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', 11);
      });

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -40)
      .attr('text-anchor', 'middle')
      .attr('font-size', 11)
      .attr('fill', '#6b7280')
      .text('Survival Rate');

    // Invisible hover overlay for tooltip
    const bisect = d3.bisector((d) => d).left;

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const dur = xScale.invert(mx);
        const roundedDur = Math.round(dur);

        if (roundedDur < 0 || roundedDur > maxDuration) {
          setTooltip(null);
          return;
        }

        const overallRate = lookupSurvival(data.durations, data.survivalRate, roundedDur);

        const fields = {};
        fieldKeys.forEach((key) => {
          const fd = data.byField[key];
          fields[key] = lookupSurvival(fd.durations, fd.survivalRate, roundedDur);
        });

        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          duration: roundedDur,
          overallRate,
          fields,
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });
  }, [data, hasData, fieldKeys]);

  if (!hasData) {
    return (
      <div style={styles.card}>
        <p style={styles.title}>Price Persistence Analysis</p>
        <p style={styles.subtitle}>How long price levels hold before changing</p>
        <div style={styles.emptyState}>No survival data available.</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <p style={styles.title}>Price Persistence Analysis</p>
      <p style={styles.subtitle}>How long price levels hold before changing</p>

      {data.medianDuration != null && (
        <div>
          <span style={styles.medianBadge}>
            Overall median persistence: <strong>{data.medianDuration}</strong> scrape periods
          </span>
        </div>
      )}

      <svg
        ref={svgRef}
        viewBox="0 0 700 380"
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', marginTop: 8 }}
      />

      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, background: '#9ca3af', borderTop: '2px dashed #9ca3af', height: 0 }} />
          <span>Overall</span>
        </div>
        {fieldKeys.map((key) => (
          <div key={key} style={styles.legendItem}>
            <span style={{ ...styles.legendSwatch, background: FIELD_COLORS[key] }} />
            <span>{FIELD_LABELS[key] || key}</span>
          </div>
        ))}
      </div>

      {tooltip && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          <div style={styles.tooltipLabel}>Period {tooltip.duration}</div>
          <div style={styles.tooltipRow}>
            <span style={{ color: '#d1d5db' }}>Overall</span>
            <span>{(tooltip.overallRate * 100).toFixed(1)}%</span>
          </div>
          {fieldKeys.map((key) => (
            <div key={key} style={styles.tooltipRow}>
              <span>
                <span style={{ ...styles.tooltipDot, background: FIELD_COLORS[key] }} />
                {FIELD_LABELS[key] || key}
              </span>
              <span>{(tooltip.fields[key] * 100).toFixed(1)}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Build step-function point pairs for a Kaplan-Meier style curve.
 * Each duration step holds its rate until the next step drops.
 */
function buildStepPoints(durations, survivalRate, maxDuration) {
  if (!durations || !survivalRate || durations.length === 0) return [];

  const points = [];
  // Start at time 0 with 100% survival
  points.push([0, 1.0]);

  for (let i = 0; i < durations.length; i++) {
    const d = durations[i];
    const rate = survivalRate[i];
    const prevRate = i === 0 ? 1.0 : survivalRate[i - 1];

    // Horizontal step at previous rate up to this duration
    points.push([d, prevRate]);
    // Vertical drop to new rate
    points.push([d, rate]);
  }

  // Extend to maxDuration at the last rate
  const lastRate = survivalRate[survivalRate.length - 1];
  const lastDur = durations[durations.length - 1];
  if (lastDur < maxDuration) {
    points.push([maxDuration, lastRate]);
  }

  return points;
}

/**
 * Look up survival rate at a given duration using step function logic.
 * Returns the rate at the most recent step <= the queried duration.
 */
function lookupSurvival(durations, survivalRate, targetDur) {
  if (!durations || !survivalRate || durations.length === 0) return 1.0;
  if (targetDur <= 0) return 1.0;

  let rate = 1.0;
  for (let i = 0; i < durations.length; i++) {
    if (durations[i] <= targetDur) {
      rate = survivalRate[i];
    } else {
      break;
    }
  }
  return rate;
}
